import {
  downloadCompanyContract,
  downloadCompanyContractDocx,
} from './contracts'
import type { PageProps, QueryDefinition } from '@/core'
import type { Company, User } from '@shared/schemas'
import type {
  CompanyContractRequest,
  SendCompanyNominationEmailResult,
} from './contracts'
import type { CompaniesFormValues } from './form'
import type { CompaniesVariables } from './variables'

export const COMPANY_KEYS = {
  all: ['companies'] as const,
  list: () => 'companies',
  expiringContracts: () => 'companies/expiring-contracts',
  detail: (id: string) => `companies/${id}`,
  search: (query: string, limit?: number) =>
    `companies/search/${query}/${limit ?? 'all'}`,
  byDoctor: (doctorId: string) => `companies/doctor/${doctorId}`,
  doctors: () => 'companies/doctors',
}

export type CompaniesQueryDefinition = [
  QueryDefinition<'companies', 'query', void, Array<Company>>,
  QueryDefinition<'expiringContracts', 'query', void, Array<Company>>,
  QueryDefinition<'doctors', 'query', void, Array<User>>,
  QueryDefinition<'downloadContract', 'mutation', CompanyContractRequest, Blob>,
  QueryDefinition<
    'downloadContractDocx',
    'mutation',
    CompanyContractRequest,
    Blob
  >,
  QueryDefinition<'deleteContract', 'mutation', CompanyContractRequest, void>,
  QueryDefinition<
    'sendNominationEmail',
    'mutation',
    CompanyContractRequest,
    SendCompanyNominationEmailResult
  >,
]

export const companiesQueries: NonNullable<
  PageProps<
    CompaniesFormValues,
    CompaniesQueryDefinition,
    CompaniesVariables
  >['queries']
> = [
  {
    key: 'companies',
    type: 'query',
    queryConfig: {
      queryKey: [COMPANY_KEYS.list()],
      endpoint: ['api', 'registry/companies'],
      enabled: true,
    },
  },
  {
    key: 'expiringContracts',
    type: 'query',
    queryConfig: {
      queryKey: [COMPANY_KEYS.expiringContracts()],
      endpoint: ['api', 'contracts/companies/expiring'],
      enabled: true,
    },
  },
  {
    key: 'doctors',
    type: 'query',
    queryConfig: {
      queryKey: [COMPANY_KEYS.doctors()],
      endpoint: ['api', 'users/doctors/all'],
      enabled: true,
    },
  },
  {
    key: 'downloadContract',
    type: 'mutation',
    mutationConfig: {
      endpoint: ['api', 'contracts/companies/:companyId/download'],
      method: 'POST',
      customRequest: downloadCompanyContract,
    },
  },
  {
    key: 'downloadContractDocx',
    type: 'mutation',
    mutationConfig: {
      endpoint: ['api', 'contracts/companies/:companyId/download/docx'],
      method: 'POST',
      customRequest: downloadCompanyContractDocx,
    },
  },
  {
    key: 'deleteContract',
    type: 'mutation',
    mutationConfig: {
      endpoint: ['api', 'contracts/companies/:companyId'],
      method: 'DELETE',
      queryKeyToInvalidate: [
        COMPANY_KEYS.list(),
        COMPANY_KEYS.expiringContracts(),
      ],
    },
  },
  {
    key: 'sendNominationEmail',
    type: 'mutation',
    mutationConfig: {
      endpoint: ['api', 'contracts/companies/:companyId/send-nomination-email'],
      method: 'POST',
      queryKeyToInvalidate: [
        COMPANY_KEYS.list(),
        COMPANY_KEYS.expiringContracts(),
      ],
    },
  },
]

export function createCompaniesQueries() {
  return companiesQueries
}

export * from './contracts'
